import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { Coins, Loader2, Sparkles, X, Gift as GiftIcon } from 'lucide-react';

/**
 * GiftPicker — modal for sending virtual gifts on tracks/reels/livestreams/posts.
 *
 * Props:
 *   open, onOpenChange
 *   contentId, contentType — where the gift is directed
 *   creatorUserId — recipient
 *   creatorName — display
 */
export default function GiftPicker({ open, onOpenChange, contentId, contentType, creatorUserId, creatorName }) {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const { requireAuth } = useGuest();

  const [catalog, setCatalog] = useState([]);
  const [balance, setBalance] = useState(0);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!isAuthenticated) {
      onOpenChange?.(false);
      requireAuth?.('send a gift');
      return;
    }
    setLoading(true);
    const headers = getAuthHeaders();
    Promise.all([
      fetch(`${API}/coins/gifts/catalog`).then(r => r.json()),
      fetch(`${API}/coins/balance`, { headers }).then(r => r.ok ? r.json() : { balance_coins: 0 }),
    ]).then(([c, b]) => {
      setCatalog(c.gifts || []);
      setBalance(b.balance_coins || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [open, isAuthenticated, getAuthHeaders, requireAuth, onOpenChange]);

  const totalCost = (selected?.cost_coins || 0) * quantity;
  const canAfford = balance >= totalCost && selected;

  const handleSend = async () => {
    if (!selected) return;
    if (!canAfford) {
      toast.error('Not enough coins — get more coins first');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/coins/gifts/send`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gift_id: selected.gift_id,
          content_id: contentId,
          content_type: contentType,
          creator_user_id: creatorUserId,
          quantity,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to send');
      } else {
        setBalance(data.new_balance);
        toast.success(`${selected.emoji} ${selected.name} × ${quantity} sent to ${creatorName || 'creator'}!`, { duration: 3500 });
        onOpenChange?.(false);
      }
    } catch {
      toast.error('Network error');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="gift-picker-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GiftIcon className="w-5 h-5 text-fuchsia-400" />
            Send a Gift {creatorName ? `to ${creatorName}` : ''}
          </DialogTitle>
        </DialogHeader>

        {/* Balance strip */}
        <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-500/10 to-fuchsia-500/10 border border-amber-500/25">
          <div className="flex items-center gap-2">
            <Coins className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-muted-foreground">Your balance</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold tabular-nums" data-testid="gift-picker-balance">{balance.toLocaleString()}</span>
            <button
              onClick={() => { onOpenChange?.(false); navigate('/coins'); }}
              className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
              data-testid="gift-picker-buy-coins"
            >
              + Get more
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" />
          </div>
        ) : (
          <>
            {/* Gift grid */}
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto" data-testid="gift-picker-grid">
              {catalog.map(g => {
                const active = selected?.gift_id === g.gift_id;
                return (
                  <button
                    key={g.gift_id}
                    onClick={() => setSelected(g)}
                    className={`p-2 rounded-xl border transition-all flex flex-col items-center gap-1 ${active ? 'border-fuchsia-500 bg-fuchsia-500/10 scale-105' : 'border-border hover:border-fuchsia-500/50 hover:bg-muted/20'}`}
                    data-testid={`gift-option-${g.gift_id}`}
                  >
                    <span className="text-2xl" style={{ filter: active ? 'drop-shadow(0 0 8px currentColor)' : 'none', color: g.color }}>{g.emoji}</span>
                    <span className="text-[10px] font-semibold truncate w-full text-center">{g.name}</span>
                    <span className="text-[10px] text-amber-400 tabular-nums flex items-center gap-0.5"><Coins className="w-2.5 h-2.5" />{g.cost_coins}</span>
                  </button>
                );
              })}
            </div>

            {/* Quantity + send */}
            {selected && (
              <div className="space-y-3 pt-3 border-t border-border" data-testid="gift-picker-actions">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Quantity</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-7 h-7 rounded-lg bg-muted/40 hover:bg-muted/60 font-bold" data-testid="gift-qty-minus">−</button>
                    <span className="w-8 text-center font-bold tabular-nums" data-testid="gift-qty-value">{quantity}</span>
                    <button onClick={() => setQuantity(q => Math.min(100, q + 1))} className="w-7 h-7 rounded-lg bg-muted/40 hover:bg-muted/60 font-bold" data-testid="gift-qty-plus">+</button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total cost</span>
                  <span className={`font-bold tabular-nums ${!canAfford ? 'text-red-400' : 'text-amber-400'}`} data-testid="gift-total-cost">
                    <Coins className="w-3.5 h-3.5 inline mr-1" />
                    {totalCost.toLocaleString()}
                  </span>
                </div>
                <Button
                  disabled={!canAfford || sending}
                  onClick={handleSend}
                  className="w-full bg-gradient-to-r from-fuchsia-600 to-orange-500 hover:from-fuchsia-700 hover:to-orange-600 gap-1.5 h-11 text-base"
                  data-testid="gift-picker-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {sending ? 'Sending…' : canAfford ? `Send ${selected.emoji} ${selected.name} × ${quantity}` : 'Not enough coins'}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
